import type { KAPLAYCtx } from '../types.ts'
import type { RoomOptions } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'

export function useFocusRoomB(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('focusRoomB', `sprites/rooms/focus-room-b.png?${cacheStr}`)
  k.loadSprite('focusRoomBHover', `sprites/rooms/focus-room-b--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON(
    'focusRoomBColliders',
    `colliders/focus-room-b.parsed.json?${cacheStr}`,
  )

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function setFocusRoomB(roomOpts?: RoomOptions) {
    roomOpts = roomOpts || {}

    const roomObj = k.add()

    k.onLoad(() => {
      const { data } = collidersJson

      roomObj.add([
        k.sprite('focusRoomB', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('background'),
        'focus_room_b_background',
        'background',
      ])

      roomObj.add([
        k.sprite('focusRoomBHover', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('hover'),
        'focus_room_b_hover',
        'hover',
      ])

      data.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        roomObj.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      roomObj.add([
        k.level([
          '      ',
          '      ',
          '      ',
          '      ',
          '1  ?  ',
          '      ',
          '      ',
        ], {
          tileWidth: getTiledSize(),
          tileHeight: getTiledSize(),
          tiles: {
            '1': () => [
              k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
              k.pos(getTiledSize(0.5), 0),
              k.area({ isSensor: true }),
              'focus_room_b_door_exit',
              'door_exit',
              'door',
            ],
            '?': () => [
              k.sprite('overlay', { anim: 'question' }),
              k.area({ isSensor: true }),
              k.layer('background'),
              'focus_room_b_info',
              'sfx_emit_action',
              'info',
            ],
          },
        }),
        k.layer('objects'),
        'focus_room_b',
      ])

      // Set player position
      k.get('player')[0]?.setPos(k.vec2(getTiledSize(1.5), getTiledSize(4)))

      const doorExitCollision = k.onCollide('player', 'focus_room_b_door_exit', () => {
        k.flash(k.BLACK, 0.5)
        roomObj.destroy()
        doorExitCollision.cancel()
        infoCollision.cancel()
        infoCollisionEnd.cancel()
        if (roomOpts.onExit) roomOpts.onExit()
      })

      const infoCollision = k.onCollide('player', 'focus_room_b_info', () => {
        showTextFrame(getLangString('focus_room_info'))
      })

      const infoCollisionEnd = k.onCollideEnd('player', 'focus_room_b_info', () => {
        showTextFrame()
      })
    })

    return roomObj
  }

  return {
    setFocusRoomB,
  }
}
